/**
 * Cover art loading via Tauri IPC — bypasses mixed-content blocking.
 *
 * WKWebView blocks `<img src="http://127.0.0.1:…">` on HTTPS pages.
 * Tauri IPC (`invoke()`) doesn't go through HTTP at all, so cover art
 * read from the on-disk cache arrives as base64 over the JS bridge.
 *
 * Two strategies:
 *   1. By hash — reads from cover_cache, then local-core/artwork/
 *   2. By folder path — extracts on-demand from audio files / sidecar images
 *      (only real filesystem paths — never stable ids like local:album:…)
 */

import { connectorInvoke, isConnectorAvailable } from './connector';
import { isHomeLibraryHost, isRemoteConnectorShell } from '../services/localLibraryIpcGate';
import { normalizeArtworkCacheKey } from '../utils/coverArt';
import {
  getBlobCoverUrl,
  putBlobCoverUrl,
  removeBlobCoverUrl,
} from '../utils/blobCoverCache';

interface CoverArtResponse {
  data: string;
  mime_type: string;
}

const _cache = new Map<string, string | null>();
const _pathCache = new Map<string, string | null>();
const _inflight = new Map<string, Promise<string | null>>();
type CoverCacheListener = (hash: string) => void;
const _cacheListeners = new Set<CoverCacheListener>();

function notifyCoverCacheLoaded(hashes: string[]) {
  for (const hash of hashes) {
    for (const listener of _cacheListeners) listener(hash);
  }
}

/** Subscribe to IPC cover-cache writes for a specific hash (per-tile refresh). */
export function subscribeCoverCacheLoaded(listener: CoverCacheListener): () => void {
  _cacheListeners.add(listener);
  return () => { _cacheListeners.delete(listener); };
}

const HASH_RE = /^[0-9a-f]{64}$/;

/** Normalize artwork hash from API (lowercase 64-char hex). */
export function normalizeCoverHash(hash: string | null | undefined): string | null {
  if (!hash || typeof hash !== 'string') return null;
  const h = hash.trim().toLowerCase();
  return HASH_RE.test(h) ? h : null;
}

/** Cache lookup key — 64-char full art or 16-char harvested thumb. */
export function normalizeCoverCacheLookupKey(hash: string | null | undefined): string | null {
  return normalizeArtworkCacheKey(hash) || normalizeCoverHash(hash);
}

const _coverWarnedKeys = new Set<string>();

function warnCoverLookupOnce(
  sessionKey: string,
  message: string,
  details: Record<string, unknown>,
): void {
  if (_coverWarnedKeys.has(sessionKey)) return;
  _coverWarnedKeys.add(sessionKey);
  console.warn(`[cover-fix] ${message}`, details);
}

/** Real album folder on disk — not `local:album:…` stable ids. */
export function pickAlbumFolderPath(album: {
  library_folder_path?: string | null;
  sample_file_path?: string | null;
  file_path?: string | null;
} | null | undefined): string | null {
  if (!album) return null;
  const explicit = album.library_folder_path;
  if (explicit && typeof explicit === 'string' && !explicit.startsWith('local:')) {
    return explicit;
  }
  const sample = album.sample_file_path || album.file_path;
  if (sample && typeof sample === 'string' && !sample.startsWith('local:')) {
    const idx = Math.max(sample.lastIndexOf('/'), sample.lastIndexOf('\\'));
    if (idx > 0) return sample.slice(0, idx);
  }
  return null;
}

function isLocalLibraryAlbum(album: { isLocalLibrary?: boolean; __source?: string; machine_id?: string } | null | undefined): boolean {
  if (!album) return false;
  if (album.__source === 'remote_tunnel') return false;
  return !!(album.isLocalLibrary || album.__source === 'local_library');
}

/** Tauri Connector viewing a home library elsewhere (no local SQLite catalog). */
export function isRemoteCoverViewerShell(): boolean {
  if (typeof window === 'undefined' || !(window as any).__TAURI__) return false;
  if (isHomeLibraryHost()) return false;
  return isRemoteConnectorShell();
}

/**
 * Load cover art from home library via Mycelium tunnel proxy (authenticated fetch → blob URL).
 */
export async function getRemoteCoverArt(
  hash: string | null | undefined,
  machineId?: string | null,
): Promise<string | null> {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return null;

  const cacheKey = `remote:${normalized}:${machineId || ''}`;
  const cached = _cache.get(cacheKey) ?? getBlobCoverUrl(cacheKey);
  if (cached !== undefined) return cached;

  const existing = _inflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      const { fetchCloudAuthenticated } = await import('../services/api');
      const qs = machineId ? `?machine_id=${encodeURIComponent(machineId)}` : '';
      const resp = await fetchCloudAuthenticated(`/api/library/remote/cover/${normalized}${qs}`);
      if (!resp.ok) {
        _cache.set(cacheKey, null);
        return null;
      }
      const blob = await resp.blob();
      const blobUrl = putBlobCoverUrl(cacheKey, blob);
      _cache.set(cacheKey, blobUrl);
      return blobUrl;
    } catch (e) {
      console.warn('[CoverIPC] remote cover fetch failed', normalized.slice(0, 12), e);
      _cache.set(cacheKey, null);
      return null;
    } finally {
      _inflight.delete(cacheKey);
    }
  })();

  _inflight.set(cacheKey, promise);
  return promise;
}

async function invokeIPC<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  return connectorInvoke<T>(cmd, args);
}

function coverResponseToDataUrl(resp: CoverArtResponse | null | undefined): string | null {
  if (!resp?.data) return null;
  return `data:${resp.mime_type};base64,${resp.data}`;
}

async function readCoverArtIPC(
  hash: string,
  preferThumb: boolean,
): Promise<CoverArtResponse | null> {
  return invokeIPC<CoverArtResponse | null>('read_cover_art', {
    hash,
    preferThumb,
  });
}

/**
 * Load a single cover by hash — requests 200px thumb first, falls back to full art on miss.
 */
async function readCoverArtWithThumbFallback(
  hash: string,
  context?: { albumId?: string | null },
): Promise<string | null> {
  try {
    if (import.meta.env?.DEV) console.debug('[cover-fix] requesting artwork', {
      albumId: context?.albumId ?? null,
      artworkHash: hash.slice(0, 12),
      source: 'read_cover_art',
      prefer_thumb: true,
    });
    const thumb = await readCoverArtIPC(hash, true);
    const thumbUrl = coverResponseToDataUrl(thumb);
    if (thumbUrl) {
      if (import.meta.env?.DEV) console.debug('[cover-fix] got thumb bytes', {
        artworkHash: hash.slice(0, 12),
        bytes: thumb!.data.length,
      });
      return thumbUrl;
    }
    const full = await readCoverArtIPC(hash, false);
    const fullUrl = coverResponseToDataUrl(full);
    if (fullUrl) {
      if (import.meta.env?.DEV) console.debug('[cover-fix] got cover bytes (full fallback)', {
        artworkHash: hash.slice(0, 12),
        bytes: full!.data.length,
      });
      return fullUrl;
    }
    warnCoverLookupOnce(`hash:${context?.albumId || hash}:${hash.slice(0, 16)}`, 'cover lookup failed', {
      albumId: context?.albumId ?? null,
      artworkHash: hash.slice(0, 16),
      primaryUrl: `read_cover_art prefer_thumb=true`,
      fallbackUrl: `read_cover_art prefer_thumb=false`,
      error: 'read_cover_art returned empty',
    });
    return null;
  } catch (e) {
    warnCoverLookupOnce(`hash:${context?.albumId || hash}:${hash.slice(0, 16)}`, 'cover lookup failed', {
      albumId: context?.albumId ?? null,
      artworkHash: hash.slice(0, 16),
      primaryUrl: `read_cover_art prefer_thumb=true`,
      fallbackUrl: `read_cover_art prefer_thumb=false`,
      error: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}

async function readCoverArtForAlbumIPC(
  albumId: string,
  preferThumb: boolean,
): Promise<CoverArtResponse | null> {
  return invokeIPC<CoverArtResponse | null>('read_cover_art_for_album', {
    albumId,
    preferThumb,
  });
}

/**
 * Load cover for a local-library album by stable/row id — same chain as GET /cover/{album_id}.
 */
export async function getCoverArtForAlbum(
  album: AlbumCoverPathInput | null | undefined,
  options?: { preferThumb?: boolean },
): Promise<string | null> {
  const albumId = String(album?.album_key || album?.id || '').trim();
  if (!albumId || !isConnectorAvailable()) return null;

  const preferThumb = options?.preferThumb !== false;
  const cacheKey = `album:${albumId}:${preferThumb ? 'thumb' : 'full'}`;
  const cached = _cache.get(cacheKey);
  if (cached !== undefined) return cached;

  const existing = _inflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      if (import.meta.env?.DEV) {
        console.debug('[cover-fix] requesting artwork by album id', {
          albumId,
          prefer_thumb: preferThumb,
        });
      }
      const resp = await readCoverArtForAlbumIPC(albumId, preferThumb);
      const dataUrl = coverResponseToDataUrl(resp);
      if (dataUrl) {
        _cache.set(cacheKey, dataUrl);
        notifyCoverCacheLoaded([albumId]);
        return dataUrl;
      }
      return null;
    } catch (e) {
      warnCoverLookupOnce(`album:${albumId}`, 'album cover lookup failed', {
        albumId,
        primaryUrl: `read_cover_art_for_album prefer_thumb=${preferThumb}`,
        fallbackUrl: 'resolveAlbumWallCoverArt (thumb IPC)',
        error: e instanceof Error ? e.message : String(e),
      });
      return null;
    } finally {
      _inflight.delete(cacheKey);
    }
  })();

  _inflight.set(cacheKey, promise);
  return promise;
}

/**
 * Load a single cover by its SHA256/blake3 hash via Tauri IPC.
 * Returns a `data:image/…;base64,…` URL or null.
 */
export async function getCoverArt(
  hash: string | null | undefined,
  context?: { albumId?: string | null; machineId?: string | null },
): Promise<string | null> {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return null;
  if (!isConnectorAvailable()) return null;

  // Remote Connector shell has no local files — always proxy via Mycelium tunnel.
  if (isRemoteCoverViewerShell()) {
    return getRemoteCoverArt(normalized, context?.machineId ?? null);
  }

  const cached = _cache.get(normalized);
  if (cached !== undefined) return cached;

  const existing = _inflight.get(normalized);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      const dataUrl = await readCoverArtWithThumbFallback(normalized, context);
      if (dataUrl) {
        _cache.set(normalized, dataUrl);
        notifyCoverCacheLoaded([normalized]);
        return dataUrl;
      }
      // Do not cache null — hash may miss disk cache while path extraction still works.
      return null;
    } finally {
      _inflight.delete(normalized);
    }
  })();

  _inflight.set(normalized, promise);
  return promise;
}

/** Load cover by full (64-char) or harvested thumb (16-char) artwork store key. */
export async function getCoverArtByCacheKey(
  hash: string | null | undefined,
  context?: { albumId?: string | null; machineId?: string | null },
): Promise<string | null> {
  const normalized = normalizeArtworkCacheKey(hash);
  if (!normalized) return null;
  if (!isConnectorAvailable()) return null;

  if (isRemoteCoverViewerShell()) {
    return getRemoteCoverArt(normalized, context?.machineId ?? null);
  }

  const cached = _cache.get(normalized);
  if (cached !== undefined) return cached;

  const existing = _inflight.get(normalized);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      const dataUrl = await readCoverArtWithThumbFallback(normalized, context);
      if (dataUrl) {
        _cache.set(normalized, dataUrl);
        notifyCoverCacheLoaded([normalized]);
        return dataUrl;
      }
      return null;
    } finally {
      _inflight.delete(normalized);
    }
  })();

  _inflight.set(normalized, promise);
  return promise;
}

/**
 * Load cover art by album folder path. The Connector extracts it
 * on-demand from embedded audio tags or sidecar images (cover.jpg, etc.).
 */
export async function getCoverArtByPath(albumPath: string | null | undefined): Promise<string | null> {
  if (!albumPath || typeof albumPath !== 'string') return null;
  if (albumPath.startsWith('local:')) return null;
  if (!isConnectorAvailable()) return null;

  const cached = _pathCache.get(albumPath);
  if (cached !== undefined) return cached;

  const key = `path:${albumPath}`;
  const existing = _inflight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      const resp = await invokeIPC<CoverArtResponse | null>('read_cover_art_from_path', { albumPath });
      if (resp && resp.data) {
        const dataUrl = `data:${resp.mime_type};base64,${resp.data}`;
        _pathCache.set(albumPath, dataUrl);
        return dataUrl;
      }
      _pathCache.set(albumPath, null);
      return null;
    } catch (e) {
      console.warn('[CoverIPC] path IPC failed for', albumPath, e);
      _pathCache.set(albumPath, null);
      return null;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, promise);
  return promise;
}

/**
 * Try hash-based lookup first, then fall back to filesystem folder path only.
 */
export async function getCoverArtWithFallback(
  embeddedHash: string | null | undefined,
  folderHash: string | null | undefined,
  albumPath: string | null | undefined,
  context?: { albumId?: string | null; machineId?: string | null; thumbHash?: string | null },
): Promise<string | null> {
  const thumb = normalizeArtworkCacheKey(context?.thumbHash);
  if (thumb) {
    const byThumb = await getCoverArtByCacheKey(thumb, context);
    if (byThumb) return byThumb;
  }
  const emb = normalizeCoverHash(embeddedHash);
  const fol = normalizeCoverHash(folderHash);
  const byHash = await getCoverArt(emb, context) || await getCoverArt(fol, context);
  if (byHash) return byHash;
  const folder = pickAlbumFolderPath({ library_folder_path: albumPath }) ?? (
    albumPath && !albumPath.startsWith('local:') ? albumPath : null
  );
  if (folder) return getCoverArtByPath(folder);
  return null;
}

/**
 * Batch-load covers by hash. Returns a Map<hash, dataUrl|null>.
 */
export async function getCoverArtBatch(
  hashes: (string | null | undefined)[]
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();
  if (!isConnectorAvailable()) return result;

  if (isRemoteCoverViewerShell()) {
    await Promise.all(
      hashes.map(async (h) => {
        const normalized = normalizeCoverHash(h);
        if (!normalized) return;
        const url = await getRemoteCoverArt(normalized, null);
        result.set(normalized, url);
      }),
    );
    return result;
  }

  const needed: string[] = [];
  for (const h of hashes) {
    const normalized = normalizeCoverHash(h);
    if (!normalized) continue;
    const cached = _cache.get(normalized);
    if (cached !== undefined) {
      result.set(normalized, cached);
    } else {
      needed.push(normalized);
    }
  }

  if (needed.length === 0) return result;

  const CHUNK = 32;
  const loadedHashes: string[] = [];
  for (let offset = 0; offset < needed.length; offset += CHUNK) {
    const slice = needed.slice(offset, offset + CHUNK);
    try {
      const thumbResponses = await invokeIPC<(CoverArtResponse | null)[]>(
        'read_cover_art_batch',
        { hashes: slice, preferThumb: true },
      );
      const fullRetry: string[] = [];
      for (let i = 0; i < slice.length; i++) {
        const hash = slice[i];
        const dataUrl = coverResponseToDataUrl(thumbResponses[i]);
        if (dataUrl) {
          _cache.set(hash, dataUrl);
          result.set(hash, dataUrl);
          loadedHashes.push(hash);
        } else {
          fullRetry.push(hash);
        }
      }
      if (fullRetry.length > 0) {
        const fullResponses = await invokeIPC<(CoverArtResponse | null)[]>(
          'read_cover_art_batch',
          { hashes: fullRetry, preferThumb: false },
        );
        for (let i = 0; i < fullRetry.length; i++) {
          const hash = fullRetry[i];
          const dataUrl = coverResponseToDataUrl(fullResponses[i]);
          if (dataUrl) {
            _cache.set(hash, dataUrl);
            result.set(hash, dataUrl);
            loadedHashes.push(hash);
          }
        }
      }
    } catch (e) {
      console.warn('[CoverIPC] batch failed:', e);
    }
    if (offset + CHUNK < needed.length) {
      await new Promise((r) => setTimeout(r, 80));
    }
  }

  if (loadedHashes.length > 0) notifyCoverCacheLoaded(loadedHashes);
  return result;
}

export function isCoverIPCAvailable(): boolean {
  return isConnectorAvailable();
}

export function getCoverFromCache(hash: string | null | undefined): string | null | undefined {
  const normalized = normalizeCoverCacheLookupKey(hash);
  if (!normalized) return undefined;
  const hit = _cache.get(normalized);
  if (hit === undefined) return undefined;
  return hit || null;
}

/** True when a resolved data URL is already in the IPC cache for this hash. */
export function hasResolvedCoverInCache(hash: string | null | undefined): boolean {
  const normalized = normalizeCoverCacheLookupKey(hash);
  if (!normalized) return false;
  const hit = _cache.get(normalized);
  return typeof hit === 'string' && hit.length > 0;
}

/** Inputs shared by AlbumGrid wall tiles and local-library detail hero. */
export type LocalAlbumCoverInputs = {
  /** Grid's primaryEmbHash: normalizeCoverHash(emb) || session detailHash. */
  primaryEmbeddedHash: string | null | undefined;
  folderHash?: string | null | undefined;
  externalHash?: string | null | undefined;
  album?: AlbumCoverPathInput | null | undefined;
};

/**
 * Single resolution entry for wall tiles and local-library detail hero.
 * Both call sites must pass the same hash inputs (especially primaryEmbeddedHash).
 */
export async function resolveLocalAlbumCoverDisplay(
  inputs: LocalAlbumCoverInputs,
): Promise<string | null> {
  return resolveAlbumWallCoverArt(
    inputs.primaryEmbeddedHash,
    inputs.folderHash ?? null,
    inputs.album ?? null,
    inputs.externalHash ?? null,
  );
}

/**
 * Same resolution chain as AlbumGrid wall tiles — thumb IPC first, then full
 * for embedded/folder hashes, then on-demand folder extraction.
 */
export async function resolveAlbumWallCoverArt(
  embeddedHash: string | null | undefined,
  folderHash: string | null | undefined,
  album: AlbumCoverPathInput | null | undefined,
  externalHash?: string | null | undefined,
): Promise<string | null> {
  const tryHash = async (hash: string | null, retryFullOnThumbMiss: boolean) => {
    const h = normalizeCoverCacheLookupKey(hash);
    if (!h) return null;
    let url = await resolveAlbumCoverArtByHash(h, album, { preferThumb: true });
    if (!url && retryFullOnThumbMiss) {
      url = await resolveAlbumCoverArtByHash(h, album, { preferThumb: false });
    }
    return url;
  };
  const emb = normalizeCoverHash(embeddedHash);
  const fol = normalizeCoverHash(folderHash);
  const thumb = normalizeArtworkCacheKey(album?.cover_art_thumb_hash);
  const ext = normalizeCoverHash(externalHash);
  const { fetchValidatedLocalThumbUrl } = await import('../utils/tileCoverArt');
  if (thumb) {
    const diskThumb = await fetchValidatedLocalThumbUrl(thumb);
    if (diskThumb) return diskThumb;
  }
  for (const entry of [[emb, true], [fol, true], [thumb, true], [ext, false]] as const) {
    const url = await tryHash(entry[0], entry[1]);
    if (url) return url;
  }
  if (isLocalLibraryAlbum(album)) {
    const folderPath = pickAlbumFolderPath(album);
    if (folderPath) return getCoverArtByPath(folderPath);
  }
  return null;
}

/**
 * Detail hero — try full-resolution variant first, then the wall tile source.
 * Pass `embeddedHash` as the grid's `primaryEmbHash` (emb || session detailHash).
 */
export async function resolveAlbumDetailHeroCover(
  embeddedHash: string | null | undefined,
  folderHash: string | null | undefined,
  album: AlbumCoverPathInput | null | undefined,
  externalHash?: string | null | undefined,
): Promise<{ url: string | null; wallFallbackUrl: string | null }> {
  const wallFallbackUrl = await resolveLocalAlbumCoverDisplay({
    primaryEmbeddedHash: embeddedHash,
    folderHash,
    externalHash,
    album,
  });

  if (!isLocalLibraryAlbum(album)) {
    return { url: wallFallbackUrl, wallFallbackUrl };
  }

  const thumbKey = normalizeArtworkCacheKey(album?.cover_art_thumb_hash);
  const emb = normalizeCoverHash(embeddedHash);
  const fol = normalizeCoverHash(folderHash);
  let primary: string | null = null;

  if (isConnectorAvailable()) {
    primary = await getCoverArtForAlbum(album, { preferThumb: false });
  }
  if (!primary) {
    for (const h of [thumbKey, emb, fol]) {
      if (!h) continue;
      primary = await resolveAlbumCoverArtByHash(h, album, { preferThumb: false });
      if (primary) break;
    }
  }

  // Local hero display matches the wall tile source; full-res primary is optional upgrade.
  return { url: wallFallbackUrl || primary, wallFallbackUrl };
}

type AlbumCoverPathInput = {
  library_folder_path?: string | null;
  sample_file_path?: string | null;
  file_path?: string | null;
  album_key?: string | null;
  id?: string | null;
  machine_id?: string | null;
  cover_art_thumb_hash?: string | null;
  external_cover_hash?: string | null;
  isLocalLibrary?: boolean;
  __source?: string;
};

/**
 * Resolve cover by hash only — no folder/path extraction. Use for external
 * enrichment art where the bytes live in cover_cache keyed by hash.
 */
export async function resolveAlbumCoverArtByHash(
  hash: string | null | undefined,
  album: AlbumCoverPathInput | null | undefined,
  options?: { preferThumb?: boolean },
): Promise<string | null> {
  const h = normalizeArtworkCacheKey(hash) || normalizeCoverHash(hash);
  if (!h) return null;
  const context = {
    albumId: album?.album_key || album?.id || null,
    machineId: album?.machine_id ?? null,
  };

  if (hasResolvedCoverInCache(h)) {
    return _cache.get(h) || null;
  }

  if (!isConnectorAvailable()) {
    const { buildLocalCoverUrl } = await import('./connector');
    const thumb = options?.preferThumb !== false;
    return buildLocalCoverUrl(h, { thumb });
  }

  if (h.length === 16) {
    return getCoverArtByCacheKey(h, context);
  }

  return getCoverArt(h, context);
}

/**
 * Same resolution chain as album detail — IPC cache hit, then hash IPC, then
 * on-demand folder/path extraction. Skips HTTP /cover/{hash} proxy URLs that
 * 404 when art is not yet extracted to the on-disk cache.
 */
export async function resolveAlbumCoverArt(
  embeddedHash: string | null | undefined,
  folderHash: string | null | undefined,
  album: AlbumCoverPathInput | null | undefined,
  options?: { preferThumb?: boolean; hashOnly?: boolean },
): Promise<string | null> {
  const thumbKey = normalizeArtworkCacheKey(album?.cover_art_thumb_hash);
  const emb = normalizeCoverHash(embeddedHash);
  const fol = normalizeCoverHash(folderHash);
  const folderPath = pickAlbumFolderPath(album);
  const context = {
    albumId: album?.album_key || album?.id || null,
    machineId: album?.machine_id ?? null,
    thumbHash: thumbKey,
  };

  if (thumbKey && hasResolvedCoverInCache(thumbKey)) {
    return _cache.get(thumbKey) || null;
  }

  for (const h of [emb, fol]) {
    if (hasResolvedCoverInCache(h)) {
      return _cache.get(h!) || null;
    }
  }

  if (!isConnectorAvailable()) {
    const { buildLocalCoverUrl } = await import('./connector');
    const { resolveLocalAlbumCoverSync } = await import('../utils/coverArt');
    const thumb = options?.preferThumb !== false;
    return resolveLocalAlbumCoverSync(album as Record<string, unknown>, { thumb })
      || buildLocalCoverUrl(emb, { thumb })
      || buildLocalCoverUrl(fol, { thumb });
  }

  if (isLocalLibraryAlbum(album) && options?.preferThumb !== false) {
    const byAlbum = await getCoverArtForAlbum(album, { preferThumb: true });
    if (byAlbum) return byAlbum;
  }

  if (options?.hashOnly) {
    if (thumbKey) {
      const byThumb = await getCoverArtByCacheKey(thumbKey, context);
      if (byThumb) return byThumb;
    }
    return getCoverArt(emb, context) || getCoverArt(fol, context);
  }

  const fromFallback = await getCoverArtWithFallback(emb, fol, folderPath, context);
  if (fromFallback) return fromFallback;

  if (isLocalLibraryAlbum(album) && options?.preferThumb === false) {
    const byAlbumFull = await getCoverArtForAlbum(album, { preferThumb: false });
    if (byAlbumFull) return byAlbumFull;
  }

  return null;
}

export function getRemoteCoverFromCache(
  hash: string | null | undefined,
  machineId?: string | null,
): string | null | undefined {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return undefined;
  const cacheKey = `remote:${normalized}:${machineId || ''}`;
  if (_cache.has(cacheKey)) return _cache.get(cacheKey);
  // Batch prefetch may have stored without machine_id — try that fallback.
  const genericKey = `remote:${normalized}:`;
  if (_cache.has(genericKey)) return _cache.get(genericKey);
  return undefined;
}

type AlbumCoverRow = {
  album_key?: string | null;
  id?: string | null;
  embedded_cover_hash?: string | null;
  folder_cover_hash?: string | null;
  external_cover_hash?: string | null;
  cover_art_url?: string | null;
  cover_art_updated_at?: string | null;
  updated_at?: string | null;
};

function albumStableKey(album: AlbumCoverRow | null | undefined): string {
  return String(album?.album_key ?? album?.id ?? '').trim();
}

function albumCoverHashSet(album: AlbumCoverRow | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const raw of [
    album?.embedded_cover_hash,
    album?.folder_cover_hash,
    album?.external_cover_hash,
  ]) {
    const h = normalizeCoverHash(raw);
    if (h) out.add(h);
  }
  return out;
}

function dropCoverHashFromCache(hash: string): void {
  for (const key of [hash, `remote:${hash}:`, `remote:${hash}`]) {
    const cached = _cache.get(key);
    if (cached && typeof cached === 'string' && cached.startsWith('blob:')) {
      removeBlobCoverUrl(key);
    }
    _cache.delete(key);
  }
  _pathCache.delete(hash);
}

/** Invalidate only cover entries whose artwork identity changed between snapshots. */
export function invalidateCoverArtCacheForAlbumChanges(
  prev: AlbumCoverRow[] | null | undefined,
  next: AlbumCoverRow[] | null | undefined,
): void {
  if (!prev?.length || !next?.length) return;

  const prevByKey = new Map<string, AlbumCoverRow>();
  for (const row of prev) {
    const key = albumStableKey(row);
    if (key) prevByKey.set(key, row);
  }

  const hashesToDrop = new Set<string>();

  for (const row of next) {
    const key = albumStableKey(row);
    if (!key) continue;
    const prior = prevByKey.get(key);
    if (!prior) continue;

    const prevHashes = albumCoverHashSet(prior);
    const nextHashes = albumCoverHashSet(row);

    for (const h of prevHashes) {
      if (!nextHashes.has(h)) hashesToDrop.add(h);
    }
    for (const h of nextHashes) {
      if (!prevHashes.has(h)) hashesToDrop.add(h);
    }

    const prevBust = String(prior.cover_art_updated_at ?? prior.updated_at ?? prior.cover_art_url ?? '');
    const nextBust = String(row.cover_art_updated_at ?? row.updated_at ?? row.cover_art_url ?? '');
    if (prevBust !== nextBust) {
      for (const h of nextHashes) hashesToDrop.add(h);
    }
  }

  for (const hash of hashesToDrop) dropCoverHashFromCache(hash);
}

/** Drop in-memory cover blobs after wipe/rescan so stale art is not reused. */
export function clearCoverArtCache(): void {
  _cache.clear();
  _pathCache.clear();
  _inflight.clear();
  import('./externalAlbumCoverResolve').then(({ clearExternalAlbumCoverCache }) => {
    clearExternalAlbumCoverCache();
  }).catch(() => {});
}

export { isLocalLibraryAlbum };
